/**
 * SystemConfig — Super Admin only.
 * Bundles Academic Year management and Full Database Backup into one tab.
 */
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Calendar, Download, Database, CheckCircle2, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';

const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('hrms-token') || ''}`,
});

export default function SystemConfig() {
  // Academic Year state
  const [years, setYears] = useState([]);
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState({ label: '', start_date: '', end_date: '', is_current: true, reset_leave_balances_on_start: false });
  const [ayLoading, setAyLoading] = useState(false);

  // Backup state
  const [collections, setCollections] = useState([]);
  const [history, setHistory] = useState([]);
  const [backupBusy, setBackupBusy] = useState(false);

  const fetchYears = async () => {
    try {
      const r = await fetch(`${API}/api/system-settings/academic-year`, { headers: authHeaders(), credentials: 'include' });
      const d = await safeJson(r);
      if (r.ok) { setYears(d.years || []); setCurrent(d.current || null); }
    } catch (e) { console.error(e); }
  };
  const fetchCollections = async () => {
    try {
      const r = await fetch(`${API}/api/backup/collections`, { headers: authHeaders(), credentials: 'include' });
      const d = await safeJson(r);
      if (r.ok) setCollections(d.collections || []);
    } catch (e) { console.error(e); }
  };
  const fetchHistory = async () => {
    try {
      const r = await fetch(`${API}/api/backup/history`, { headers: authHeaders(), credentials: 'include' });
      const d = await safeJson(r);
      if (r.ok) setHistory(d.history || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchYears(); fetchCollections(); fetchHistory(); }, []);

  const saveYear = async () => {
    if (!form.label || !form.start_date || !form.end_date) {
      toast.error('Please fill label, start date and end date'); return;
    }
    setAyLoading(true);
    try {
      const r = await fetch(`${API}/api/system-settings/academic-year`, {
        method: 'POST', headers: authHeaders(), credentials: 'include',
        body: JSON.stringify(form),
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.detail || 'Save failed');
      toast.success(`Academic Year "${form.label}" saved`);
      setForm({ label: '', start_date: '', end_date: '', is_current: true, reset_leave_balances_on_start: false });
      await fetchYears();
    } catch (e) { toast.error(e.message); } finally { setAyLoading(false); }
  };

  const setAsCurrent = async (label) => {
    try {
      const r = await fetch(`${API}/api/system-settings/academic-year/${encodeURIComponent(label)}/set-current`, {
        method: 'PUT', headers: authHeaders(), credentials: 'include',
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message); fetchYears();
    } catch (e) { toast.error(e.message); }
  };

  const deleteYear = async (label) => {
    if (!window.confirm(`Delete academic year "${label}"?`)) return;
    try {
      const r = await fetch(`${API}/api/system-settings/academic-year/${encodeURIComponent(label)}`, {
        method: 'DELETE', headers: authHeaders(), credentials: 'include',
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d.detail || 'Delete failed');
      toast.success(d.message); fetchYears();
    } catch (e) { toast.error(e.message); }
  };

  const downloadBackup = async () => {
    setBackupBusy(true);
    try {
      const r = await fetch(`${API}/api/backup/download`, { headers: authHeaders(), credentials: 'include' });
      if (!r.ok) {
        const d = await safeJson(r);
        throw new Error(d.detail || 'Backup failed');
      }
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Pull filename from header if present
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `sgi-hrms-backup-${Date.now()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
      fetchHistory();
    } catch (e) { toast.error(e.message); } finally { setBackupBusy(false); }
  };

  const totalDocs = collections.reduce((sum, c) => sum + (c.count > 0 ? c.count : 0), 0);

  return (
    <div className="space-y-6" data-testid="system-config-panel">
      {/* Academic Year */}
      <Card data-testid="academic-year-card">
        <CardHeader className="flex flex-row items-center gap-2">
          <Calendar className="w-5 h-5 text-indigo-600" />
          <CardTitle>Academic Year</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Set the active academic year. Displayed system-wide on Dashboard, reports and PDFs.
          </p>

          {current && (
            <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-sm" data-testid="current-ay">
              <span className="font-semibold text-indigo-900">Current AY:</span>{' '}
              <span className="font-mono">{current.label}</span> · {current.start_date} → {current.end_date}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label htmlFor="ay-label">Label</Label>
              <Input id="ay-label" placeholder="2025-26" value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ay-label-input" />
            </div>
            <div>
              <Label htmlFor="ay-start">Start Date</Label>
              <Input id="ay-start" type="date" value={form.start_date}
                onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="ay-start-input" />
            </div>
            <div>
              <Label htmlFor="ay-end">End Date</Label>
              <Input id="ay-end" type="date" value={form.end_date}
                onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="ay-end-input" />
            </div>
            <Button onClick={saveYear} disabled={ayLoading} data-testid="ay-save-btn">
              {ayLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Save
            </Button>
          </div>
          <div className="flex items-center gap-3 text-sm">
            <Switch checked={form.is_current} onCheckedChange={(v) => setForm({ ...form, is_current: v })} data-testid="ay-current-switch" />
            <span>Set as current</span>
            <Switch checked={form.reset_leave_balances_on_start} onCheckedChange={(v) => setForm({ ...form, reset_leave_balances_on_start: v })} data-testid="ay-reset-switch" />
            <span>Reset leave balances at year start</span>
          </div>

          {years.length > 0 && (
            <div className="border rounded-lg overflow-hidden" data-testid="ay-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="p-2 text-left">Label</th><th className="p-2 text-left">Start</th><th className="p-2 text-left">End</th><th className="p-2 text-left">Current</th><th className="p-2 text-right">Actions</th></tr>
                </thead>
                <tbody>
                  {years.map((y) => (
                    <tr key={y.label} className="border-t" data-testid={`ay-row-${y.label}`}>
                      <td className="p-2 font-mono">{y.label}</td>
                      <td className="p-2">{y.start_date}</td>
                      <td className="p-2">{y.end_date}</td>
                      <td className="p-2">{y.is_current ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : '—'}</td>
                      <td className="p-2 text-right space-x-2">
                        {!y.is_current && (
                          <Button size="sm" variant="outline" onClick={() => setAsCurrent(y.label)} data-testid={`ay-set-current-${y.label}`}>Set current</Button>
                        )}
                        <Button size="sm" variant="destructive" onClick={() => deleteYear(y.label)} data-testid={`ay-delete-${y.label}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Backup */}
      <Card data-testid="backup-card">
        <CardHeader className="flex flex-row items-center gap-2">
          <Database className="w-5 h-5 text-emerald-600" />
          <CardTitle>Database Backup</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Download a full backup of every collection as a ZIP archive (JSON files). Store it securely off-site.
          </p>

          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold" data-testid="backup-collections-count">{collections.length}</div>
              <div className="text-xs text-muted-foreground">Collections</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold" data-testid="backup-docs-count">{totalDocs.toLocaleString()}</div>
              <div className="text-xs text-muted-foreground">Documents</div>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <div className="text-2xl font-bold" data-testid="backup-history-count">{history.length}</div>
              <div className="text-xs text-muted-foreground">Past backups</div>
            </div>
          </div>

          <Button size="lg" className="w-full" onClick={downloadBackup} disabled={backupBusy} data-testid="backup-download-btn">
            {backupBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download Full Backup (ZIP)
          </Button>

          {history.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr><th className="p-2 text-left">When</th><th className="p-2 text-left">By</th><th className="p-2 text-right">Size</th><th className="p-2 text-right">Docs</th></tr>
                </thead>
                <tbody>
                  {history.slice(0, 10).map((h) => (
                    <tr key={h.backup_id} className="border-t" data-testid={`backup-row-${h.backup_id}`}>
                      <td className="p-2 text-xs">{new Date(h.created_at).toLocaleString()}</td>
                      <td className="p-2">{h.created_by}</td>
                      <td className="p-2 text-right">{(h.size_bytes / 1024).toFixed(1)} KB</td>
                      <td className="p-2 text-right">{h.total_documents?.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
